/**
 * @module components/Settings/AITab
 * @description Pestaña de configuración del asistente IA en el modal de Settings.
 *
 * Permite al usuario ingresar y guardar la API key de Gemini para habilitar
 * el asistente IA del sistema. La key se guarda en `localStorage` (nunca en DB)
 * para no exponerla en el servidor.
 *
 * Incluye un botón de prueba de conexión que verifica la key contra la API de Gemini
 * antes de guardarla. El estado de la conexión se muestra con íconos de éxito/error.
 *
 * @see geminiClient.ts para saveAPIKey(), getSavedAPIKey() y testConnection()
 * @see SettingsModal que renderiza esta pestaña
 */
// =====================================================
// AI TAB - CONFIGURACIÓN DE ASISTENTE DE IA
// =====================================================

import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Sparkles, Key, Check, X, Loader, AlertCircle, ExternalLink } from 'lucide-react';
import { saveAPIKey, getSavedAPIKey, removeAPIKey, testConnection } from '../../lib/ai/geminiClient';

export const AITab = forwardRef((props, ref) => {
  const [apiKey, setApiKey] = useState(getSavedAPIKey() || '');
  const [showApiKey, setShowApiKey] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const [testMessage, setTestMessage] = useState('');

  useImperativeHandle(ref, () => ({
    saveChanges: async () => {
      if (apiKey && apiKey !== getSavedAPIKey()) {
        saveAPIKey(apiKey);
      }
    }
  }));

  const handleTestConnection = async () => {
    if (!apiKey) {
      setTestResult('error');
      setTestMessage('Por favor, ingresa una API key primero');
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      // Guardar temporalmente para probar
      saveAPIKey(apiKey);

      const success = await testConnection();

      if (success) {
        setTestResult('success');
        setTestMessage('¡Conexión exitosa! El asistente de IA está listo para usar.');
      } else {
        setTestResult('error');
        setTestMessage('Error de conexión. Verifica tu API key.');
      }
    } catch (error: any) {
      setTestResult('error');
      setTestMessage(error?.message || 'Error al probar la conexión');
    } finally {
      setIsTesting(false);
    }
  };

  const handleRemoveKey = () => {
    if (confirm('¿Estás seguro de eliminar la API key? El asistente de IA dejará de funcionar.')) {
      removeAPIKey();
      setApiKey('');
      setTestResult(null);
      setTestMessage('');
    }
  };

  const currentKey = getSavedAPIKey();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="p-2 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Configuración de IA
          </h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Configura el asistente de cumplimiento con inteligencia artificial
        </p>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
          ¿Qué es el Asistente de IA?
        </h4>
        <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
          El asistente utiliza inteligencia artificial para ayudarte con:
        </p>
        <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
          <li>Responder preguntas sobre ISO 27001/27002</li>
          <li>Sugerir evidencias apropiadas para cada control</li>
          <li>Generar planes de implementación con tareas</li>
          <li>Analizar gaps de cumplimiento</li>
          <li>Proporcionar guía contextual y mejores prácticas</li>
        </ul>
      </div>

      {/* API Key Configuration */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Key className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
            API Key de Google Gemini
          </h4>
        </div>

        <div className="space-y-4">
          {/* Estado actual */}
          {currentKey && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                <span className="text-sm font-medium text-green-900 dark:text-green-100">
                  API Key configurada
                </span>
              </div>
              <p className="text-xs text-green-800 dark:text-green-200 mt-1">
                El asistente de IA está activo y listo para usar
              </p>
            </div>
          )}

          {/* Input de API Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Google Gemini API Key
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="AIza..."
                className="w-full px-4 py-2 pr-24 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                {showApiKey ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Obtén tu API key en{' '}
              <a
                href="https://aistudio.google.com/app/apikey"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
              >
                Google AI Studio
                <ExternalLink className="w-3 h-3" />
              </a>
            </p>
          </div>

          {/* Botones de acción */}
          <div className="flex gap-3">
            <button
              onClick={handleTestConnection}
              disabled={!apiKey || isTesting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isTesting ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Probando...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Probar Conexión
                </>
              )}
            </button>

            {currentKey && (
              <button
                onClick={handleRemoveKey}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Eliminar
              </button>
            )}
          </div>

          {/* Resultado del test */}
          {testResult && (
            <div
              className={`p-3 rounded-lg border ${
                testResult === 'success'
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              }`}
            >
              <div className="flex items-start gap-2">
                {testResult === 'success' ? (
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                )}
                <p
                  className={`text-sm ${
                    testResult === 'success'
                      ? 'text-green-800 dark:text-green-200'
                      : 'text-red-800 dark:text-red-200'
                  }`}
                >
                  {testMessage}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Seguridad y Privacidad */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-yellow-900 dark:text-yellow-100 mb-2 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          Nota sobre Seguridad y Privacidad
        </h4>
        <ul className="text-sm text-yellow-800 dark:text-yellow-200 space-y-1">
          <li>
            • <strong>Almacenamiento local:</strong> La API key se guarda en tu navegador (localStorage).
            No se envía a nuestros servidores.
          </li>
          <li>
            • <strong>Uso de Google Gemini:</strong> Las consultas se envían a Google para procesamiento.
            Revisa su{' '}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline"
            >
              política de privacidad
            </a>.
          </li>
          <li>
            • <strong>Datos sensibles:</strong> Evita incluir información confidencial en las consultas.
          </li>
          <li>
            • <strong>Costos:</strong> Gemini tiene un nivel gratuito generoso. Verifica tu{' '}
            <a
              href="https://ai.google.dev/pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline"
            >
              cuota y límites
            </a>.
          </li>
        </ul>
      </div>

      {/* Modelo y Configuración Avanzada */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Configuración del Modelo
        </h4>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Modelo</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Gemini 1.5 Flash - Rápido y gratuito
              </p>
            </div>
            <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full">
              Activo
            </span>
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">RAG (Retrieval Augmented Generation)</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Respuestas contextuales con base de conocimiento ISO
              </p>
            </div>
            <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full">
              Habilitado
            </span>
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Caché de Respuestas</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Optimización de costos y velocidad
              </p>
            </div>
            <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full">
              Habilitado
            </span>
          </div>
          <div className="flex items-center justify-between py-2 border-t border-gray-200 dark:border-gray-700 pt-3">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Límite Diario de Consultas</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Límite conservador para proteger tu cuota gratuita
              </p>
            </div>
            <span className="text-xs px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full font-semibold">
              50 / día
            </span>
          </div>
        </div>
      </div>

      {/* Ayuda */}
      <div className="text-sm text-gray-600 dark:text-gray-400">
        <p className="mb-2">
          <strong>¿Necesitas ayuda?</strong>
        </p>
        <ul className="list-disc list-inside space-y-1">
          <li>
            Consulta la{' '}
            <a
              href="https://ai.google.dev/gemini-api/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              documentación de Gemini API
            </a>
          </li>
          <li>Gemini gratuito: 1500 consultas/día, 15/minuto (límite del sistema: 50/día)</li>
          <li>Verifica que tu API key esté activa en Google AI Studio</li>
          <li>El contador se resetea automáticamente cada día</li>
        </ul>
      </div>
    </div>
  );
});

AITab.displayName = 'AITab';
